import { tSourceDefault } from 'components/@types';
import { IDataGridColumn } from 'components/data-grid/model';
import { ModuleExportColumn } from 'components/hooks/export-excel/models';
import { isValid } from 'date-fns';

export const FORMAT_DATE = 'dd/MM/yyyy';
export const FORMAT_DATE_TIME = `${FORMAT_DATE} HH:mm`;
export const HTML_STORE_URL = 'https://storewiz';
export const calcFilterFullName = ({
  firstName = 'firstName',
  lastName = 'lastName',
  value,
}: {
  firstName?: string;
  lastName?: string;
  value: string;
}) => {
  return `(${firstName}.replace(" ", String.Empty) + ${lastName}.replace(" ", String.Empty)).contains("${value.replace(
    / /gi,
    '',
  )}")`;
};
export const calcSearchMember = <Model>({ value }: { name: keyof Model; value: string }) => {
  const fields = [
    'fullName',
    'email',
    'companyName',
    'position',
    'stripeProductPrice',
    'stripeKey',
  ] as const;

  return value
    ? `(${fields
        .map((field) => {
          switch (field) {
            case 'fullName':
              return calcFilterFullName({ value });
            default:
              return `${field}.contains("${value}")`;
          }
        })
        .join(' || ')})`
    : undefined;
};

const getColumnField = (fieldGridColumn: string): string => {
  const arr = fieldGridColumn.split(' ');
  return arr[arr.length - 1];
};
export const gidColumnsToExcel = (columns: IDataGridColumn[]): ModuleExportColumn[] => {
  return columns.map((column) => {
    return {
      title: column.title,
      field: getColumnField(column.field),
      // @ts-ignore
      type: column.type,
      // @ts-ignore
      valueTemplate: column.valueTemplate,
    };
  });
};

export const prepareRequest = <T extends { [x: string]: any | null } = {}>(data: T) => {
  const keys = Object.keys(data) as (keyof T)[];
  return keys.reduce((acc, key) => {
    // @ts-ignore
    const value = data[key] === '' ? null : data[key];
    
    // Escape quotes in string values to prevent server-side parsing errors
    // if (typeof value === 'string' && value) {
    //   value = value.replace(/'/g, "\\'");
    // }
    
    // @ts-ignore
    acc[key] = value;
    return acc;
  }, data);
};

export const addZero = (value: number) => {
  return value > 9 ? value.toString() : '0' + value;
};

export const createConstFromEnum = (
  Enum: any[],
  useValue?: boolean,
  number?: boolean,
): tSourceDefault[] => {
  return Enum.slice(0, Enum.length / (useValue ? 1 : 2)).map(([id, title]) => ({
    id: number ? Number(id) : id,
    title,
  }));
};

export const StringToLabelKey = (value: string) => {
  return value
    ? value.replace(/[A-Z]/g, (str: string, i) => (i > 0 ? '-' : '') + str.toLowerCase())
    : value;
};

export const parseResponseDate = <T>(data: any, keys: string[]): T => {
  Object.keys(data).forEach((key) => {
    if (keys.includes(key)) {
      const date_ = data[key] ? new Date(data[key]) : null;
      data[key] = date_ && isValid(date_) ? date_ : null;
    }
  });
  return data;
};

export const REPORT_FILE_TYPE =
  '.svg,.txt,.tiff,.jpg,.jpeg,.png,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.htm,.html,.msg,.eml,.xlsm';

export enum ORDER_BY {
  ASC = 'asc',
  DESC = 'desc',
  DEFAULT = '',
}

export interface ISetOrder {
  field: string;
  order: ORDER_BY;
}

export const changeOrder = (
  orderName: string,
  sortOrder: ISetOrder,
  setSortOrder: (value: ISetOrder) => void,
) => {
  if (orderName !== sortOrder.field) {
    return setSortOrder({
      field: orderName,
      order: ORDER_BY.DESC,
    });
  }
  let updatedOrder = ORDER_BY.DEFAULT;

  if (sortOrder.order === ORDER_BY.DEFAULT) {
    updatedOrder = ORDER_BY.DESC;
  } else if (sortOrder.order === ORDER_BY.DESC) {
    updatedOrder = ORDER_BY.ASC;
  }

  setSortOrder({
    field: updatedOrder ? orderName : '',
    order: updatedOrder,
  });
};

export const tabsProps = (index: number) => ({
  id: `scrollable-auto-tab-${index}`,
  'aria-controls': `scrollable-auto-tabpanel-${index}`,
});

export const translateLabels = (t: (value: string) => string, labels: string[] | string) => {
  if (Array.isArray(labels)) {
    return labels.map((item) => t(item)).join(' ');
  }

  return labels ? t(labels) : '';
};

export const getIsConflictOfInterests = (value: number) => {
  switch (value) {
    case 0:
      return 'No';
    case 1:
      return 'Yes';
    case 2:
      return 'None';
    default:
      return '';
  }
};

export const saveFileToDisk = (file: File): void => {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const sanitizeTitle = (title: string): string => {
  return title
    .replace(/\\/g, '') // remove backslashes
    .replace(/"/g, '') // remove double quotes
    .replace(/'/g, '') // remove single quotes
    .replace(/\n/g, ' ') // replace line breaks with spaces
    .replace(/\r/g, ' ') // replace carriage return with spaces
    .replace(/\t/g, ' ') // replace tabs with spaces
    .replace(/\s+/g, ' ') // replace multiple spaces with one
    .trim(); // remove extra spaces at the beginning and end
};

export const queryEncoder = (input: any): string => {
  if (typeof input !== 'string') {
    return input;
  }
  return input
    .replace(/&&/g, '__and__')
    .replace(/\|\|/g, '__or__')
    .replace(/>=/g, '__goe__')
    .replace(/<=/g, '__loe__')
    .replace(/==/g, '__eq__')
    .replace(/!=/g, '__neq__')
    .replace(/>/g, '__gt__')
    .replace(/</g, '__lt__')
    .replace(/\(/g, '__lp__')
    .replace(/\)/g, '__rp__')
    .replace(/\?/g, '__q__')
    .replace(/:/g, '__c__')
    .replace(/\//g, '__slash__')
    .replace(/"/g, '__dq__') // optional if quotes cause problems
    .replace(/'/g, '__sq__')
    .replace(/#/g, '__hash__');
};

export const queryEncoderObject = (input: Record<string, any>): Record<string, any> => {
  Object.keys(input).forEach((key) => {
    input[key] = queryEncoder(input[key]);
  });
  return input;
};

export const parseGetUrlToPost = (
  url: string,
  cancelToken?: any,
): [string, Record<string, string> | undefined, any] => {
  const firstQuestionMarkIndex = url.indexOf('?');
  if (firstQuestionMarkIndex === -1) {
    return [url, undefined, { skipSourceUpdate: true, cancelToken }];
  }
  const baseUrl = url.substring(0, firstQuestionMarkIndex);
  const queryString = url.substring(firstQuestionMarkIndex + 1);
  const queryParts = queryString.split('&');
  const data: Record<string, string> = {};

  queryParts.forEach((part) => {
    const index = part.indexOf('=');
    if (index !== -1) {
      const key = part.substring(0, index);
      const value = part.substring(index + 1);
      data[key] = queryEncoder(decodeURIComponent(value));
    }
  });

  return [
    baseUrl,
    Object.keys(data).length ? data : undefined,
    { skipSourceUpdate: true, cancelToken },
  ];
};

export const base64ToBlob = (dataurl: string): Blob => {
  const arr = dataurl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : '';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export const createFileFormData = (
  base64: string,
  fileName: string,
  fieldName: string = 'file',
) => {
  const blob = base64ToBlob(base64);
  const file = new File([blob], fileName, { type: blob.type });
  const formData = new FormData();
  formData.append(fieldName, file);
  return formData;
};

export const fileToBase64 = (file: Blob) => {
  return new Promise<string>((res, rej) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      if (!e.target) {
        return rej('error-file-type');
      }
      return res(String(e.target.result));
    };
    fileReader.onerror = rej;
    fileReader.readAsDataURL(file);
  });
};

export const stringToBase64 = (htmlString: string): string => {
  return btoa(
    encodeURIComponent(htmlString).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16)),
    ),
  );
};

export const sanitizeFileName = (fileName: string): string => {
  return fileName.replace(/[^\p{L}\p{N}_\-. ]/gu, '').trim();
  // const encoded = encodeURIComponent(fileName);
  // return encoded
  //   .replace(/%22/g, '')
  //   .replace(/%2F/g, '-')
  //   .replace(/%3F/g, '-')
  //   .replace(/%3A/g, '-')
  //   .replace(/%2A/g, '')
  //   .replace(/%7C/g, '-')
  //   .replace(/%5C/g, '-')
  //   .replace(/%3C/g, '')
  //   .replace(/%3E/g, '')
  //   .replace(/%5B/g, '')
  //   .replace(/%5D/g, '')
  //   .replace(/%7B/g, '')
  //   .replace(/%7D/g, '');
};
